import React, { forwardRef, useEffect, useState } from 'react';

import cn from 'classnames';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { CalendarIcon } from 'assets/icons';
import './dateTimeField.css';
import { SelectField } from './SelectField';
import { useIsMobile } from 'hooks/useIsMobile';

type InputSizeType = 'md' | 'lg';

type DateTimeFieldProps = {
  label?: string;
  placeholderFormat?: string;
  errorMessage?: string;
  wrapperClassName?: string;
  size?: InputSizeType;
  value?: Date | null;
  minTime?: Date | null;
  maxTime?: Date | null;
  maxDate?: Date | null;
  minDate?: Date | null;
  onChange: (value: Date | null) => void;
};

type CustomInputProps = {
  value: string;
  onClick: () => void;
};

const sizeClassMap: Record<InputSizeType, string> = {
  md: 'h-11 pl-3 py-2 rounded-md',
  lg: 'h-[68px] px-6 py-[18px] rounded-xl',
};

export const DateTimeField: React.FC<DateTimeFieldProps> = ({
  label,
  minTime,
  maxTime,
  minDate,
  maxDate,
  placeholderFormat = 'MM.DD.YYYY, HH:MM',
  size,
  wrapperClassName,
  errorMessage,
  value,
  onChange,
}) => {
  const sizeClass = sizeClassMap[size || 'lg'];
  const topClass = cn('absolute', label ? (size === 'md' ? 'top-6 right-3' : 'top-9 right-6') : 'top-5 right-6');

  const isMobile = useIsMobile();

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(({ value, onClick }, ref) => (
    <div className={cn('relative flex justify-between', wrapperClassName)} onClick={onClick}>
      <label className="w-full flex flex-col mb-1 text-blue-500 text-xs font-medium leading-[17px] dark:text-white-400">
        {label}
        <input
          className={cn(
            'w-full bg-white-400 border border-gray-800 focus:border-gray-200',
            'dark:bg-white-400/10 dark:text-white-400 dark:placeholder-sky dark:disabled:bg-white-400/[.04]',
            'focus:shadow-hover-input dark:focus:shadow-hover-dark-input text-gray-600 text-sm font-normal leading-[140%]',
            sizeClass,
          )}
          placeholder={placeholderFormat}
          value={value}
          ref={ref}
          readOnly
        />
      </label>
      <CalendarIcon className={topClass} />
      {errorMessage && (
        <p className="absolute bottom-0 left-0 text-xs text-red-400 font-medium leading-[17px]">{errorMessage}</p>
      )}
    </div>
  ));

  let hoursIndex = placeholderFormat.indexOf('HH');
  if (hoursIndex === -1) {
    hoursIndex = placeholderFormat.indexOf('hh');
  }
  const timeFormat = hoursIndex > -1 ? placeholderFormat.slice(hoursIndex) : 'HH:mm';

  return (
    <div className="date-time-field w-full">
      <DatePicker
        wrapperClassName="w-full"
        selected={value}
        onChange={date => onChange(!date || !maxDate ? date : date > maxDate ? maxDate : date)}
        timeIntervals={1}
        withPortal={isMobile}
        timeInputLabel="Time:"
        showTimeInput
        shouldCloseOnSelect={false}
        customTimeInput={
          <CustomTimeInput
            isAmPm={placeholderFormat.includes('hh')}
            minTime={minTime || new Date(0)}
            maxTime={maxTime || new Date(24 * 60 * 60 * 1000 - 1)}
          />
        }
        timeFormat={timeFormat}
        dateFormat={placeholderFormat}
        maxDate={maxDate}
        minDate={minDate}
        minTime={minTime || new Date(0)}
        maxTime={maxTime || new Date(24 * 60 * 60 * 1000 - 1)}
        customInput={<CustomInput value={value ? value.toString() : ''} onClick={() => {}} />}
      />
    </div>
  );
};

type CustomTimeInputProps = {
  value?: string;
  val?: Date | null;
  onChange?: (newDate: string) => void;
  minTime?: Date;
  maxTime?: Date;
  stepMinutes?: number;
  isAmPm?: boolean;
};

export default function CustomTimeInput({
  value = '00:00',
  onChange,
  minTime,
  maxTime,
  stepMinutes = 1,
  isAmPm = false,
}: CustomTimeInputProps) {
  const hours = Array.from({ length: isAmPm ? 12 : 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 / stepMinutes }, (_, i) => i * stepMinutes);

  const {
    hh: selectedHour,
    hh12: selectedHour12,
    mm: selectedMinute,
  } = parseTimeHHMM(value) || { hh: 0, hh12: 0, mm: 0 };
  const [ampm, setAmPm] = useState<'AM' | 'PM'>(selectedHour > 11 ? 'PM' : 'AM');

  useEffect(() => {
    setAmPm(selectedHour > 11 ? 'PM' : 'AM');
  }, [selectedHour]);

  const handleHourChange = (val: string) => {
    const parsedHour = parseInt(val, 10);
    const hour = isAmPm ? parsedHour + (ampm === 'PM' ? 12 : 0) : parsedHour;
    // Compute the min and max minute allowed for the selected hour
    let minMinute = 0;
    let maxMinute = 59;
    if (minTime && hour === minTime.getHours()) {
      minMinute = minTime.getMinutes();
    }
    if (maxTime && hour === maxTime.getHours()) {
      maxMinute = maxTime.getMinutes();
    }

    // Clamp currently selectedMinute to the allowed minute range for the picked hour
    let clampedMinute = Math.max(minMinute, Math.min(selectedMinute, maxMinute));

    onChange?.(`${hour}:${clampedMinute}`);
  };

  // Implements handleAmPmChange and uses it for the AM/PM select, carefully respecting minTime/maxTime
  const handleMinuteChange = (val: string) => {
    const minute = parseInt(val, 10);
    onChange?.(`${selectedHour}:${minute}`);
  };

  const handleAmPmChange = (val: string) => {
    const newAmPm = val as 'AM' | 'PM';
    setAmPm(newAmPm);

    // Compute intended new hour (convert between AM/PM)
    let newHour = selectedHour;
    if (newAmPm === 'AM' && selectedHour >= 12) {
      newHour = selectedHour - 12;
    } else if (newAmPm === 'PM' && selectedHour < 12) {
      newHour = selectedHour + 12;
    }

    // Clamp newHour to min/max time if needed
    let minHour = minTime ? minTime.getHours() : 0;
    let maxHour = maxTime ? maxTime.getHours() : 23;
    if (newHour < minHour) newHour = minHour;
    if (newHour > maxHour) newHour = maxHour;

    // Clamp minute as well, in case newHour is at min or max bounds
    let minMinute = 0,
      maxMinute = 59;
    if (minTime && newHour === minHour) minMinute = minTime.getMinutes();
    if (maxTime && newHour === maxHour) maxMinute = maxTime.getMinutes();
    let clampedMinute = Math.max(minMinute, Math.min(selectedMinute, maxMinute));

    onChange?.(`${newHour}:${clampedMinute}`);
  };

  return (
    <div className="flex gap-0 items-center">
      <SelectField
        options={hours.map(h => ({
          value: h,
          label: h.toString().padStart(2, '0'),
          isDisabled: !isHourWithinRange(isAmPm ? h + (ampm === 'PM' ? 12 : 0) : h, minTime, maxTime),
        }))}
        value={isAmPm ? selectedHour12 : selectedHour}
        onChange={e => handleHourChange(e?.toString?.() || '')}
        selectClassName="!w-[68px]"
        menuPlacement="top"
      />
      <span className="text-gray-500 dark:text-white-400 text-sm font-medium leading-6">:</span>
      <SelectField
        options={minutes.map(m => ({
          value: m,
          label: m.toString().padStart(2, '0'),
          isDisabled: !isMinuteWithinRange(m, minTime, maxTime, selectedHour),
        }))}
        value={selectedMinute}
        onChange={e => handleMinuteChange(e?.toString?.() || '')}
        menuPlacement="top"
        selectClassName="!w-[68px]"
      />
      {isAmPm && (
        <SelectField
          options={[
            { value: 'AM', label: 'AM' },
            { value: 'PM', label: 'PM' },
          ]}
          value={ampm}
          onChange={e => handleAmPmChange(e?.toString?.() || '')}
          selectClassName="!w-[72px]"
        />
      )}
    </div>
  );
}

function getHour(date?: Date | null): number | undefined {
  return date?.getHours?.();
}

function isHourWithinRange(hour: number, min?: Date | null, max?: Date | null): boolean {
  const minHour = getHour(min);
  const maxHour = getHour(max);
  return (minHour === undefined || hour >= minHour) && (maxHour === undefined || hour <= maxHour);
}

function getMinute(date?: Date | null): number | undefined {
  return date?.getMinutes?.();
}

// Only allow minutes in the range if the selected hour is the same as min or max hour; otherwise, allow all minutes
function isMinuteWithinRange(minute: number, min?: Date | null, max?: Date | null, selectedHour?: number): boolean {
  const minHour = min?.getHours?.();
  const maxHour = max?.getHours?.();
  const minMinute = getMinute(min);
  const maxMinute = getMinute(max);

  if (selectedHour !== undefined) {
    if (minHour !== undefined && selectedHour === minHour) {
      return minMinute === undefined || minute >= minMinute;
    }
    if (maxHour !== undefined && selectedHour === maxHour) {
      return maxMinute === undefined || minute <= maxMinute;
    }
    return true;
  }

  return (minMinute === undefined || minute >= minMinute) && (maxMinute === undefined || minute <= maxMinute);
}

function parseTimeHHMM(time: string): { hh: number; hh12: number; mm: number } | null {
  if (!time) return null;
  const parts = time.trim().split(':');
  if (parts.length !== 2) return null;
  let hh = parseInt(parts[0], 10);
  const mm = parseInt(parts[1], 10);

  if (isNaN(hh) || isNaN(mm) || mm < 0 || mm > 59) {
    return null;
  }

  return { hh, hh12: hh % 12, mm };
}
